import React from 'react';
import { motion } from 'framer-motion';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CheckCircle, CircleDashed } from 'lucide-react';
import { TableCell } from '@/components/ui/table';
import { formatCurrency } from '@/lib/portfolioCalculations';
type EnhancedPortfolioRowLite = {
  id: string;
  name: string;
  portfolio_type?: string;
  portfolio_size?: number;
};

interface TotalsByClass {
  equity: number;
  fixed_income: number;
  cash: number;
  alternative: number;
  multi_asset: number;
}

interface PortfolioTableRowProps {
  portfolio: EnhancedPortfolioRowLite;
  index: number;
  currency?: string;
  totals: TotalsByClass;
  completed: boolean;
  isSelected: boolean;
  onToggleSelected: () => void;
  onRowClick: () => void;
  typeLabel: string;
}

export const PortfolioTableRow: React.FC<PortfolioTableRowProps> = ({
  portfolio,
  index,
  currency,
  totals,
  completed,
  isSelected,
  onToggleSelected,
  onRowClick,
  typeLabel,
}) => {
  return (
    <motion.tr
      key={portfolio.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2, delay: index * 0.02 }}
      className={"border-0 hover:bg-muted/10 bg-white even:bg-gray-50/50 transition-colors cursor-pointer"}
      onClick={onRowClick}
    >
      <TableCell className="w-[40px] min-w-[40px] pl-4 pr-2 py-1.5 align-middle" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center">
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelected}
            aria-label={`Select ${portfolio.name}`}
            className="h-3.5 w-3.5"
          />
        </div>
      </TableCell>
      <TableCell className="px-3 py-1.5 align-middle">
        <div className="font-medium text-[13px] truncate max-w-[180px]" title={portfolio.name}>{portfolio.name}</div>
      </TableCell>
      <TableCell className="px-3 py-1.5 align-middle">
        <span className="text-[13px]">{typeLabel}</span>
      </TableCell>
      <TableCell className="w-[44px] min-w-[44px] px-2 py-1.5 align-middle">
        <div className="flex items-center justify-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                {completed ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <CircleDashed className="h-4 w-4 text-gray-500" />
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p>{completed ? 'Completed' : 'In Progress'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </TableCell>
      <TableCell className="px-2 py-1 text-right align-middle whitespace-nowrap tabular-nums text-xs">
        <span className="text-xs leading-4 font-medium font-mono tabular-nums">{formatCurrency(totals.equity, currency, { showDecimals: true, locale: 'en-CA' })}</span>
      </TableCell>
      <TableCell className="px-2 py-1 text-right align-middle whitespace-nowrap tabular-nums text-xs">
        <span className="text-xs leading-4 font-medium font-mono tabular-nums">{formatCurrency(totals.fixed_income, currency, { showDecimals: true, locale: 'en-CA' })}</span>
      </TableCell>
      <TableCell className="px-2 py-1 text-right align-middle whitespace-nowrap tabular-nums text-xs">
        <span className="text-xs leading-4 font-medium font-mono tabular-nums">{formatCurrency(totals.cash, currency, { showDecimals: true, locale: 'en-CA' })}</span>
      </TableCell>
      <TableCell className="px-2 py-1 text-right align-middle whitespace-nowrap tabular-nums text-xs">
        <span className="text-xs leading-4 font-medium font-mono tabular-nums">{formatCurrency(totals.alternative, currency, { showDecimals: true, locale: 'en-CA' })}</span>
      </TableCell>
      <TableCell className="px-2 py-1 text-right align-middle whitespace-nowrap tabular-nums text-xs">
        <span className="text-xs leading-4 font-medium font-mono tabular-nums">{formatCurrency(totals.multi_asset, currency, { showDecimals: true, locale: 'en-CA' })}</span>
      </TableCell>
      <TableCell className="px-2 py-1 pr-4 text-right align-middle whitespace-nowrap tabular-nums text-xs">
        <span className="text-xs leading-4 font-semibold font-mono tabular-nums">{formatCurrency(portfolio.portfolio_size || 0, currency, { showDecimals: true, locale: 'en-CA' })}</span>
      </TableCell>
    </motion.tr>
  );
};
